import { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from './ui';

/**
 * SIGNATURE CAPTURE (parent spec §6.6): a typed name in a script face is
 * LEGALLY EQUIVALENT to a drawn squiggle — the mark is evidence of intent,
 * not identity; the session, IP, timestamps and presented hash prove who.
 * Both kinds therefore gate on the same two facts and nothing else: consent
 * to electronic records, and an explicit press of "I agree and sign".
 *
 * Dependency-free (raw 2d canvas), no react-dom, host-agnostic — the same
 * component signs in platform-app (React 19), super-admin (React 18) and the
 * public OTP page. The host provides the surrounding document view and the
 * scrolled-to-end tracking; this component only captures the mark.
 */

export interface SignatureResult {
  kind: 'typed' | 'drawn';
  typedName?: string;
  /** Base64 PNG (no data-URL prefix) when kind === 'drawn'. */
  signatureImagePngBase64?: string;
}

export interface SignaturePadProps {
  /** The signer's legal name — prefills the typed tab. */
  signerName: string;
  /** Disabled until the host confirms the signer scrolled to the end. */
  scrolledToEnd: boolean;
  busy?: boolean;
  onSign: (result: SignatureResult) => void;
  onConsentChange?: (consented: boolean) => void;
  /**
   * WHICH SURFACE THIS IS SIGNING ON.
   *
   * `dark` is the original and stays the default, so super-admin and the public
   * OTP page render byte for byte what they always have. `ruby` is the RubyTill
   * livery — crimson chrome, ink on paper — for the tenant-facing signing
   * screen, which is drawn on light stone rather than dark-900.
   *
   * It changes the INK as well as the paper: a near-white stroke is invisible
   * on a white canvas, which would let somebody "sign" and see nothing appear.
   */
  variant?: 'dark' | 'ruby';
}

/** The brand's crimson. Kept local — this package has no theme to read from. */
const RUBY = '#DC143C';

export function SignaturePad({
  signerName, scrolledToEnd, busy, onSign, onConsentChange, variant = 'dark',
}: SignaturePadProps) {
  const ruby = variant === 'ruby';
  const [tab, setTab] = useState<'typed' | 'drawn'>('typed');
  const [typedName, setTypedName] = useState(signerName);
  const [consented, setConsented] = useState(false);
  const [hasDrawn, setHasDrawn] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const drawing = useRef(false);
  const last = useRef<{ x: number; y: number } | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || tab !== 'drawn') return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.lineWidth = 2.2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    // Ink follows the paper: near-white on the dark pad, near-black on the
    // ruby one. Getting this wrong makes a signature that cannot be seen.
    ctx.strokeStyle = ruby ? '#1c1917' : '#f5f5f4';
  }, [tab, ruby]);

  const pos = (e: { clientX: number; clientY: number }) => {
    const canvas = canvasRef.current!;
    const rect = canvas.getBoundingClientRect();
    return {
      x: ((e.clientX - rect.left) / rect.width) * canvas.width,
      y: ((e.clientY - rect.top) / rect.height) * canvas.height,
    };
  };

  const strokeTo = useCallback((p: { x: number; y: number }) => {
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx || !last.current) { last.current = p; return; }
    ctx.beginPath();
    ctx.moveTo(last.current.x, last.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    last.current = p;
    setHasDrawn(true);
  }, []);

  const start = (e: React.PointerEvent<HTMLCanvasElement>) => {
    (e.target as HTMLCanvasElement).setPointerCapture(e.pointerId);
    drawing.current = true;
    last.current = pos(e);
  };
  const move = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!drawing.current) return;
    e.preventDefault();
    strokeTo(pos(e));
  };
  const end = () => { drawing.current = false; last.current = null; };

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (canvas && ctx) ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasDrawn(false);
  };

  const consent = (next: boolean) => {
    setConsented(next);
    onConsentChange?.(next);
  };

  const canSign = consented && scrolledToEnd && !busy
    && (tab === 'typed' ? typedName.trim().length > 0 : hasDrawn);

  const submit = () => {
    if (!canSign) return;
    if (tab === 'typed') {
      onSign({ kind: 'typed', typedName: typedName.trim() });
    } else {
      const dataUrl = canvasRef.current?.toDataURL('image/png') ?? '';
      onSign({ kind: 'drawn', signatureImagePngBase64: dataUrl.split(',')[1] ?? '' });
    }
  };

  /*
    THE RUBY PAD.

    Same component, same evidence, same two gates — only the surface changes:
    a crimson band across the top, a white card, and a signing area drawn as
    PAPER, with the ruled line and the ✕ a paper form would have. Somebody
    about to put their name to a contract should recognise the thing they are
    writing on.
  */
  if (ruby) {
    return (
      <div className="overflow-hidden rounded-xl border border-stone-200 bg-white shadow-sm">
        <div
          className="flex items-center justify-between gap-2 px-3 py-2.5"
          style={{ background: `linear-gradient(135deg, ${RUBY}14, ${RUBY}05)`, borderBottom: `1px solid ${RUBY}26` }}
        >
          <span className="text-[10.5px] font-extrabold uppercase tracking-[.1em] text-stone-500">
            Your signature
          </span>
          <div className="flex gap-1">
            {(['typed', 'drawn'] as const).map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => setTab(t)}
                className="rounded-full px-3 py-1.5 text-[11.5px] font-bold"
                style={tab === t
                  ? { background: RUBY, color: '#fff' }
                  : { color: '#78716c' }}
              >
                {t === 'typed' ? 'Type' : 'Draw'}
              </button>
            ))}
          </div>
        </div>

        <div className="p-3">
          {tab === 'typed' ? (
            <div>
              <input
                className="min-h-[44px] w-full rounded-lg border border-stone-300 bg-white px-3 py-2.5 text-[13px] text-stone-900 placeholder:text-stone-400"
                value={typedName}
                onChange={(e) => setTypedName(e.target.value)}
                placeholder="Your full legal name"
              />
              {/* THE PAPER. The name is shown as it will be recorded, on the
                  ruled line it will sit on. */}
              <div
                className="mt-3 rounded-lg px-4 pb-2 pt-6"
                style={{ background: `linear-gradient(180deg, #fff, ${RUBY}08)`, border: `1px solid ${RUBY}20` }}
              >
                <p
                  className="min-h-[38px] text-[30px] leading-none text-stone-900"
                  style={{ fontFamily: "'Snell Roundhand', 'Segoe Script', 'Brush Script MT', cursive" }}
                >
                  {typedName.trim()}
                </p>
                <div className="mt-1 flex items-baseline gap-2 border-t pt-1" style={{ borderColor: `${RUBY}40` }}>
                  <span className="text-[13px] font-bold" style={{ color: RUBY }}>✕</span>
                  <span className="text-[10px] font-semibold uppercase tracking-[.12em] text-stone-400">
                    Sign here
                  </span>
                </div>
              </div>
            </div>
          ) : (
            <div>
              <canvas
                ref={canvasRef}
                width={560}
                height={160}
                className="w-full touch-none rounded-lg bg-white"
                style={{ border: `1px dashed ${RUBY}59` }}
                onPointerDown={start}
                onPointerMove={move}
                onPointerUp={end}
                onPointerLeave={end}
              />
              <div className="mt-2 flex items-center justify-between gap-2">
                <span className="text-[10px] font-semibold uppercase tracking-[.12em] text-stone-400">
                  Draw with your finger or mouse
                </span>
                <button
                  type="button"
                  onClick={clear}
                  disabled={!hasDrawn}
                  className="min-h-[36px] rounded-lg border border-stone-300 bg-white px-3 text-[12px] font-semibold text-stone-600 disabled:opacity-40"
                >
                  Clear
                </button>
              </div>
            </div>
          )}

          <label className="mt-3 flex min-h-[44px] cursor-pointer items-start gap-2.5 text-[12px] leading-relaxed text-stone-600">
            <input
              type="checkbox"
              className="mt-0.5 h-4 w-4"
              style={{ accentColor: RUBY }}
              checked={consented}
              onChange={(e) => consent(e.target.checked)}
            />
            <span>
              I agree to use electronic records and signatures, and I have read this document.
              My electronic signature is legally binding, the same as a handwritten one.
            </span>
          </label>

          {!scrolledToEnd && (
            <p className="mt-2 rounded-lg border border-amber-200 bg-amber-50 px-2.5 py-2 text-[11.5px] text-amber-800">
              Scroll to the end of the document before signing — the record proves you saw all of it.
            </p>
          )}

          <button
            type="button"
            disabled={!canSign}
            onClick={submit}
            className="mt-3 min-h-[46px] w-full rounded-xl px-4 text-[13.5px] font-bold text-white disabled:cursor-not-allowed disabled:opacity-40"
            style={{
              background: `linear-gradient(135deg, #e8365a, ${RUBY} 55%, #9F1239)`,
              boxShadow: `0 10px 26px -12px ${RUBY}`,
            }}
          >
            {busy ? 'Signing…' : 'I agree and sign'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-zinc-700 bg-dark-800 p-4">
      <div className="mb-3 flex gap-2">
        {(['typed', 'drawn'] as const).map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setTab(t)}
            className={`rounded-full px-3.5 py-1.5 text-[12px] font-semibold ${
              tab === t ? 'bg-emerald-500/15 text-emerald-300' : 'text-stone-400 hover:text-white'
            }`}
          >
            {t === 'typed' ? 'Type your name' : 'Draw your signature'}
          </button>
        ))}
      </div>

      {tab === 'typed' ? (
        <div>
          <input
            className="w-full rounded-lg border border-zinc-700 bg-dark-900 px-3 py-2.5 text-white"
            value={typedName}
            onChange={(e) => setTypedName(e.target.value)}
            placeholder="Your full legal name"
          />
          {typedName.trim() && (
            <p
              className="mt-3 border-b border-zinc-600 pb-1 text-2xl text-stone-100"
              style={{ fontFamily: "'Snell Roundhand', 'Segoe Script', 'Brush Script MT', cursive" }}
            >
              {typedName.trim()}
            </p>
          )}
        </div>
      ) : (
        <div>
          <canvas
            ref={canvasRef}
            width={560}
            height={160}
            className="w-full touch-none rounded-lg border border-dashed border-zinc-600 bg-dark-900"
            onPointerDown={start}
            onPointerMove={move}
            onPointerUp={end}
            onPointerLeave={end}
          />
          <div className="mt-2">
            <Button onClick={clear} disabled={!hasDrawn}>Clear</Button>
          </div>
        </div>
      )}

      <label className="mt-4 flex min-h-[44px] cursor-pointer items-start gap-2.5 text-[12.5px] leading-relaxed text-stone-300">
        <input
          type="checkbox"
          className="mt-0.5"
          checked={consented}
          onChange={(e) => consent(e.target.checked)}
        />
        <span>
          I agree to use electronic records and signatures, and I have read this document.
          My electronic signature is legally binding, the same as a handwritten one.
        </span>
      </label>

      {!scrolledToEnd && (
        <p className="mt-2 text-[12px] text-amber-300">
          Scroll to the end of the document before signing — the record proves you saw all of it.
        </p>
      )}

      <div className="mt-3">
        <Button variant="primary" disabled={!canSign} onClick={submit}>
          {busy ? 'Signing…' : 'I agree and sign'}
        </Button>
      </div>
    </div>
  );
}
